import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert, Button, Popconfirm, Select, Skeleton, Tag, Typography } from 'antd'
import { KeyRound, RefreshCw, Terminal } from 'lucide-react'
import { createBotMcpConfig, getBotMcpEndpoint } from '@/features/profile/index.ts'
import { useTRPC } from '@/trpc/react.ts'
import type { BotDetailData } from './bot-detail.tsx'

export function BotCredentials({ bot }: { bot: BotDetailData }) {
  const trpc = useTRPC()
  const cache = useQueryClient()
  const [key, setKey] = useState<string>()
  const [groupType, setGroupType] = useState<'all' | 'internal' | 'external'>(
    bot.kind === 'customer' ? 'all' : 'internal'
  )
  const endpoint = getBotMcpEndpoint({
    origin: window.location.origin,
    development: import.meta.env.DEV,
    configuredUrl: import.meta.env.VITE_MCP_URL
  })
  const mcpConfig = createBotMcpConfig({ endpoint, botId: bot.id, key })
  const config = [
    `SUPER_MEGA_MCP_URL=${endpoint}`,
    `SUPER_MEGA_GATEWAY_KEY=${key || '<填写已保存的机器人 MCP 密钥>'}`,
    `SUPER_MEGA_BOT_ID=${bot.id}`,
    `SUPER_MEGA_WECOM_GROUP_TYPE=${groupType}`,
    'WECOM_ALLOW_ALL_USERS=true',
    'DEEPSEEK_API_KEY=<填写实际 DeepSeek API Key>'
  ].join('\n')
  const status = useQuery(trpc.mcpCredential.botStatus.queryOptions({ botId: bot.id }))
  const hasActiveKey = Boolean(status.data && !status.data.revokedAt)
  const refresh = () => cache.invalidateQueries(trpc.mcpCredential.botStatus.queryFilter({ botId: bot.id }))
  const rotate = useMutation(
    trpc.mcpCredential.botRotate.mutationOptions({
      onSuccess: async (value) => {
        setKey(value.key)
        await refresh()
      }
    })
  )
  const revoke = useMutation(
    trpc.mcpCredential.botRevoke.mutationOptions({
      onSuccess: async () => {
        setKey(undefined)
        await refresh()
      }
    })
  )
  const error = rotate.error ?? revoke.error ?? status.error
  return (
    <div className="space-y-5">
      {error && <Alert type="error" showIcon title={error.message} />}
      <div className="min-w-0">
        <section className="bg-surface border-border min-w-0 overflow-hidden rounded-xl border">
          <div className="border-border flex flex-wrap items-center justify-between gap-3 border-b p-5 sm:px-6">
            <h2 className="text-content m-0 flex items-center gap-2.5 text-base font-semibold">
              <KeyRound className="text-accent size-5" />
              MCP 密钥
            </h2>
            {!status.isPending && !status.error && (
              <Tag className="m-0!" color={hasActiveKey ? 'success' : 'default'}>
                {hasActiveKey ? '有效' : '未启用'}
              </Tag>
            )}
          </div>
          <div className="flex flex-col gap-5 p-5 sm:p-6">
            <p className="text-content-muted m-0 text-sm leading-6">
              供受信任的 Hermes 网关访问业务数据。客服密钥需取得群授权；内部助手按已配置权限跨客户查询。
            </p>
            {status.isPending && <Skeleton active paragraph={{ rows: 1 }} title={false} />}
            {hasActiveKey && (
              <div className="flex items-center justify-between gap-3 text-sm">
                <span className="text-content-muted">当前密钥</span>
                <code className="text-content font-mono">{status.data?.prefix}…</code>
              </div>
            )}
            <div className="bg-surface-muted border-border flex flex-col gap-3 rounded-lg border p-4">
              <div className="text-content flex items-center gap-2 text-sm font-medium">
                <Terminal className="size-4" />
                配置到 Hermes
              </div>
              <p className="text-content-muted m-0 text-sm leading-6">
                将下面配置保存到运行此机器人的 Hermes Profile .env 文件。Profile 名称只用于部署，不需要在后台填写。
              </p>
              <label className="text-sm" htmlFor="bot-group-type">
                使用群类型
              </label>
              <Select
                id="bot-group-type"
                className="w-full sm:max-w-xs"
                value={groupType}
                onChange={setGroupType}
                disabled={bot.kind === 'internal'}
                options={
                  bot.kind === 'customer'
                    ? [
                        { value: 'all', label: '通用（内部群和外部群）' },
                        { value: 'internal', label: '内部群' },
                        { value: 'external', label: '外部群' }
                      ]
                    : [{ value: 'internal', label: '内部群' }]
                }
              />
              <Typography.Paragraph
                copyable={{ text: config }}
                className="mb-0! font-mono text-xs! leading-6! break-all whitespace-pre-wrap"
              >
                {config}
              </Typography.Paragraph>
              <p className="text-content-muted m-0 text-sm leading-6">
                MCP 客户端连接配置如下。客服机器人还需由受信任网关注入当前群授权；仅连接时只提供连通性检查。
              </p>
              <Typography.Paragraph
                copyable={{ text: mcpConfig }}
                className="mb-0! font-mono text-xs! leading-6! break-all whitespace-pre-wrap"
              >
                {mcpConfig}
              </Typography.Paragraph>
              <p className="text-content-soft m-0 text-xs leading-5">
                企微机器人 ID 和 Secret 在“基本信息”中维护，由 Hermes
                启动时读取。群类型由部署配置声明，请与实际使用的群保持一致。
              </p>
            </div>
            {key && <Alert type="success" title="新密钥只显示一次，请复制上方配置并保存到 Hermes Profile 的 .env" />}
          </div>
          <div className="border-border flex flex-wrap items-center justify-between gap-3 border-t px-5 py-4 sm:px-6">
            <Popconfirm
              title="刷新后旧密钥立即失效，是否继续？"
              onConfirm={() => rotate.mutateAsync({ botId: bot.id })}
            >
              <Button type="primary" icon={<RefreshCw className="size-4" />} loading={rotate.isPending}>
                生成 / 刷新密钥
              </Button>
            </Popconfirm>
            <Popconfirm title="撤销机器人 MCP 密钥？" onConfirm={() => revoke.mutateAsync({ botId: bot.id })}>
              <Button danger type="text" loading={revoke.isPending}>
                撤销密钥
              </Button>
            </Popconfirm>
          </div>
        </section>
      </div>
    </div>
  )
}
